/**
 * Real `PolicyAPI` implementation backed by the HTTP client.
 *
 *   loadState(site_id)   → GET    /v1/sites/:site_id/policy
 *                          200 + `{ policy: Policy | null, draft: PolicyDraft | null }`
 *   saveDraft(...)        → POST   /v1/sites/:site_id/policy/draft
 *                          200 + `{ draft: PolicyDraft }`
 *   discardDraft(...)     → DELETE /v1/sites/:site_id/policy/draft
 *                          200 + `{ ok: true }`
 *   publish(...)          → PUT    /v1/sites/:site_id/policy
 *                          201 + `{ policy: Policy }`
 *
 * Publish runs the DB INSERT + Cloudflare SITE_POLICIES KV push
 * atomically inside the same withTenantTx, so a successful 201 means
 * the new policy is durable AND visible at the edge.
 */

import type { Policy, Rule, SiteId } from '@sill/types';
import type {
  PolicyAPI,
  PolicyDraft,
  PolicyPublishOptions,
  PolicyState,
  PolicyTestOutcome,
  PolicyTestSample,
  R01ActiveAgents,
  RedTeamRunResult,
  ShadowLogQuery,
  ShadowLogResponse,
  TemplateDescriptor,
} from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export interface RealPolicyDeps {
  readonly http: HttpClient;
}

interface PolicyStateResponse {
  policy: Policy | null;
  draft: PolicyDraft | null;
}

interface DraftResponse {
  draft: PolicyDraft;
}

interface PolicyOkResponse {
  policy: Policy;
}

export function createRealPolicyApi(deps: RealPolicyDeps): PolicyAPI {
  return {
    async loadState(site_id: SiteId): Promise<PolicyState> {
      const res = await deps.http.request<PolicyStateResponse>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/policy`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
      return {
        active: res.policy ?? null,
        draft: res.draft ?? null,
      };
    },

    async saveDraft(
      site_id: SiteId,
      rules: readonly Rule[],
    ): Promise<PolicyDraft> {
      const res = await deps.http.request<DraftResponse>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(site_id)}/policy/draft`,
        body: { rules, grammar_version: 1 },
      });
      if (res === undefined || res.draft === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res.draft;
    },

    async discardDraft(site_id: SiteId): Promise<void> {
      const res = await deps.http.request<{ ok: boolean }>({
        method: 'DELETE',
        path: `/v1/sites/${encodeURIComponent(site_id)}/policy/draft`,
      });
      if (res === undefined) {
        throw new ApiError('bad_response', null);
      }
    },

    async publish(
      site_id: SiteId,
      version: string,
      rules: readonly Rule[],
      options: PolicyPublishOptions,
    ): Promise<Policy> {
      // ADR-0121 §3 must-hold 2 — the PUT publish handler now REQUIRES
      // `confirm_replace_active: true` + `if_match_policy_id` (strict
      // match against the current active policy_id, `null` on first
      // publish). `source` is optional ('editor' default); absent →
      // server treats it as 'editor'. We send it explicitly so the
      // wire trace is unambiguous.
      //
      // ADR-0177 — `source: 'template'` additionally requires
      // `template_slug` + `template_version` (server 400s
      // `template_unknown` on an unresolved pair and `version_mismatch`
      // when the `version` label doesn't equal `${slug}-v${version}`).
      const body: {
        version: string;
        rules: readonly Rule[];
        grammar_version: number;
        confirm_replace_active: boolean;
        if_match_policy_id: string | null;
        source?: 'editor' | 'import' | 'template';
        template_slug?: string;
        template_version?: string;
      } = {
        version,
        rules,
        grammar_version: 1,
        confirm_replace_active: options.confirm_replace_active,
        if_match_policy_id: options.if_match_policy_id,
      };
      if (options.source !== undefined) body.source = options.source;
      if (options.template_slug !== undefined) {
        body.template_slug = options.template_slug;
      }
      if (options.template_version !== undefined) {
        body.template_version = options.template_version;
      }
      const res = await deps.http.request<PolicyOkResponse>({
        method: 'PUT',
        path: `/v1/sites/${encodeURIComponent(site_id)}/policy`,
        body,
      });
      if (res === undefined || res.policy === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res.policy;
    },

    async testRule(
      site_id: SiteId,
      rule: Rule,
      sample: PolicyTestSample,
    ): Promise<PolicyTestOutcome> {
      const res = await deps.http.request<PolicyTestOutcome>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(site_id)}/policy/test`,
        body: { rule, sample },
      });
      if (res === undefined || res.outcome === undefined) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async runRedTeam(site_id: SiteId, site_key?: string): Promise<RedTeamRunResult> {
      const res = await deps.http.request<RedTeamRunResult>({
        method: 'POST',
        path: `/v1/sites/${encodeURIComponent(site_id)}/red-team`,
        // ADR-0089 Phase 2b — the raw site_key (when the dashboard has it,
        // remembered from create) unlocks the live edge-shadow stage. The
        // origin verifies it against the stored hash before firing; omitting it
        // (or sending an unknown key) just runs 2a-only.
        body: site_key !== undefined && site_key !== '' ? { site_key } : {},
      });
      if (res === undefined || !Array.isArray(res.verdicts)) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async listTemplates(): Promise<TemplateDescriptor[]> {
      // ADR-0177 — GET /v1/policy-templates. Global catalog (no
      // per-site path); owner/admin gated server-side. The dashboard's
      // starter-template picker calls this on modal open.
      const res = await deps.http.request<{ templates: TemplateDescriptor[] }>({
        method: 'GET',
        path: '/v1/policy-templates',
      });
      if (res === undefined || !Array.isArray(res.templates)) {
        throw new ApiError('bad_response', null);
      }
      return res.templates;
    },

    async listR01ActiveAgents(site_id: SiteId): Promise<R01ActiveAgents> {
      // r01 publish-time footgun guard (2026-09-23 vbout.com fix) —
      // GET /v1/sites/:site_id/r01/active-agents. Powers the Rules
      // r01 impact warning and the Red-team "Enable r01" prefill.
      // The server bounds the payload to 25 rows.
      const res = await deps.http.request<R01ActiveAgents>({
        method: 'GET',
        path: `/v1/sites/${encodeURIComponent(site_id)}/r01/active-agents`,
      });
      if (res === undefined || !Array.isArray(res.items)) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },

    async loadShadowLog(
      site_id: SiteId,
      opts?: ShadowLogQuery,
    ): Promise<ShadowLogResponse> {
      // ADR-0117 Slice A — GET /v1/sites/:site_id/policy/shadow-log.
      // Returns { draft_fingerprint: string | null, decisions: ShadowDecision[] }.
      // Slice A reads against an empty table — the producer (Slice B)
      // lands separately. The panel renders an honest "no decisions
      // yet" state in the meantime.
      const params = new URLSearchParams();
      if (opts?.since !== undefined) params.set('since', opts.since);
      if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
      const qs = params.toString();
      const path = `/v1/sites/${encodeURIComponent(site_id)}/policy/shadow-log${
        qs.length > 0 ? `?${qs}` : ''
      }`;
      const res = await deps.http.request<ShadowLogResponse>({
        method: 'GET',
        path,
      });
      if (
        res === undefined
        || !Array.isArray(res.decisions)
        || (res.draft_fingerprint !== null
          && typeof res.draft_fingerprint !== 'string')
      ) {
        throw new ApiError('bad_response', null);
      }
      return res;
    },
  };
}
